import React from "react";
import { blogPosts } from "./blog/data/blogPosts";
import { Link, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import * as UsefulButtons from "../components/UsefulButtons";
import { Helmet } from "react-helmet-async";

export default function BlogListing() {
  const [searchParams, setSearchParams] = useSearchParams();
  const topics = searchParams.getAll("topic");
  const activeTopics = topics.length > 0 ? topics.map((t) => t.toLowerCase()) : ["all"];

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/journey", label: "Our Journey" },
    { to: "#", label: "Blogs & Insights" },
    { to: "/get-in-touch", label: "Support" },
  ];

  const allTopics = ["All", ...new Set(blogPosts.map((post) => post.topic))];

  const filteredPosts =
    activeTopics.includes("all")
      ? blogPosts
      : blogPosts.filter((post) =>
          activeTopics.includes(post.topic.toLowerCase())
        );

  const handleTopicClick = (clickedTopic) => {
    const current = searchParams.getAll("topic");
    const isSelected = current.includes(clickedTopic);

    let nextTopics = [];

    if (clickedTopic === "All") {
      nextTopics = [];
    } else {
      nextTopics = isSelected
        ? current.filter((t) => t !== clickedTopic)
        : [...current.filter((t) => t !== "All"), clickedTopic];
    }

    const params = new URLSearchParams();
    nextTopics.forEach((t) => params.append("topic", t));
    setSearchParams(params);
  };

  return (
    <div className="scroll-pt-24">
      <Header links={navLinks} />
      <Helmet>
        <title>Blogs & Insights | DataPulse AI</title>
        <meta name="title" content="Blogs & Insights | DataPulse AI" />
        <meta name="description" content="Explore practical insights on automation, AI, and business growth. Learn how DataPulse AI is helping real businesses scale smarter and work faster." />
        <link rel="canonical" href="https://www.mydatapulse.co.uk/blog" />
        <meta property="og:title" content="Blogs & Insights | DataPulse AI" />
        <meta property="og:description" content="Explore practical insights on automation, AI, and business growth. Learn how DataPulse AI is helping real businesses scale smarter and work faster." />
        <meta property="og:image" content="https://www.mydatapulse.co.uk/icons/blog-image-og.jpg" />
        <meta property="og:image:alt" content="DataPulse AI blogs insights Page View" />
        <meta property="og:url" content="https://www.mydatapulse.co.uk/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="DataPulse AI" />
        <meta name="robots" content="index, follow" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Blogs & Insights | DataPulse AI" />
        <meta name="twitter:description" content="Explore practical insights on automation, AI, and business growth. Learn how DataPulse AI is helping real businesses scale smarter and work faster." />
        <meta name="twitter:image" content="https://www.mydatapulse.co.uk/icons/blog-image-og.jpg" />
        <meta name="twitter:image:alt" content="DataPulse AI blogs insights Page View" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Blogs & Insights | DataPulse AI",
            "url": "https://www.mydatapulse.co.uk/blog",
            "description": "Explore practical insights on automation, AI, and business growth from the DataPulse AI team.",
            "publisher": {
              "@type": "Organization",
              "name": "DataPulse AI",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.mydatapulse.co.uk/logo-clean-large.png"
              }
            }
          })}
        </script>

      </Helmet>
      <main>
        <section className="px-4">
          <div className="max-w-screen-xl px-6 pt-32 mx-auto text-center">
            <h1 className="text-5xl font-extrabold text-gray-900">
              DataPulse AI Blogs & Insights
              <p className="mt-4 text-lg text-gray-500 font-semibold">
                Explore how AI and automation are transforming modern businesses.
              </p>
            </h1>
          </div>
        </section>
        <section className="bg-white pt-6 pb-8">
          <div className="max-w-screen-xl mx-auto px-6 flex flex-wrap justify-center gap-4">
            {allTopics.map((t) => (
              <button
                key={t}
                onClick={() => handleTopicClick(t)}
                className={`px-4 py-2 text-sm font-medium rounded-full border transition ${
                  activeTopics.includes(t.toLowerCase())
                    ? "bg-purple-600 text-white border-purple-600"
                    : "bg-gray-100 text-gray-800 border-gray-300 hover:bg-gray-200"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </section>
        <section className="py-12">
          <div className="max-w-screen-xl mx-auto px-6">
            <div className="grid xl:gap-10 sm:grid-cols-2 lg:grid-cols-4">
              {filteredPosts.map((post) => (
                <Link
                  to={`/blog/${post.slug}`}
                  key={post.slug}
                  className="group p-2 block bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300 mb-6"
                >
                  <div style={{ maxWidth: "384px", margin: "0 auto" }}>
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        paddingBottom: "100%",
                        overflow: "hidden",
                        borderRadius: "12px",
                      }}
                    >
                      <img
                        src={post.image}
                        alt={post.title}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                  </div>
                  <div className="p-5">
                    <h2 className="group-title-purple text-lg font-semibold text-gray-900 group-hover:underline">
                      {post.title}
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      {post.date} • {post.readingTime}
                    </p>
                    <p className="text-gray-700 mt-3 text-sm">
                      {post.subheading}
                    </p>
                    <span className="inline-block mt-4 text-title-purple text-sm font-medium hover:underline">
                      Read more →
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
        <section className="bg-white">
          <div className="max-w-screen-xl px-4 pt-20 pb-8 py-16 mx-auto text-center">
            <h3 className="mb-4 text-3xl font-extrabold tracking-tight text-gray-900">
              Designed for companies like yours
            </h3>
            <p className="mb-8 font-light text-gray-500 sm:text-xl">
              We partner with forward-thinking, growth-driven businesses that
              recognise the long-term value of innovation.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <UsefulButtons.ChatOnWhatsAppButton text="Get in touch" />
              <UsefulButtons.ChatWithTeamPageButton text="📋 Send us a message" />
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
